/**
 * Feature: Marketing-Focused Dashboard
 * /features/marketing-dashboard
 *
 * The first CRM interface designed by marketers, for roofing business owners.
 * SoftwareApplication + FAQ schema.
 */
import {
  FeatureFaq,
  FeatureSignup,
  FeatureHeroBadge,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  BarChart3,
  DollarSign,
  Search,
  Star,
  ArrowRight,
  Megaphone,
  PieChart,
  Eye,
} from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';

const faqs = [
  {
    question: 'How is this different from the CRM dashboard?',
    answer:
      'The Marketing Dashboard focuses specifically on marketing performance: cost-per-lead, channel ROI, SEO rankings, brand reputation, and ad spend attribution. Your CRM dashboard shows sales pipeline and project status. Both are included — no extra charge.',
  },
  {
    question: 'Can I connect my Google Ads and Facebook Ads accounts?',
    answer:
      'Yes. Connect your ad accounts via one-click integrations. You will see ad spend vs closed revenue in one view, allowing you to calculate true cost-per-acquisition and shut down underperforming campaigns in real-time.',
  },
  {
    question: 'Do I need a marketing background to use the dashboard?',
    answer:
      'Not at all. We designed it for roofing business owners, not marketers. Every metric is translated into dollars and business outcomes. You see "this ad campaign generated $42,000 in closed deals" — not jargon like CTR or impressions.',
  },
  {
    question: 'How does reputation management work?',
    answer:
      'We monitor Google Business Profile, Yelp, and social mentions. You get alerts for new reviews, one-click response templates, and automated review request drips to customers after job completion.',
  },
];

export const MarketingDashboardFeaturePage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Marketing Command Center',
    description:
      'The first CRM dashboard designed by marketers for roofing business owners. Track cost-per-lead, channel ROI, SEO health, and brand reputation — all connected to actual revenue.',
    featureList: [
      'Cost-per-lead analytics',
      'Channel ROI tracking',
      'Google Ads integration',
      'SEO health monitoring',
      'Brand reputation management',
      'Revenue attribution',
    ],
    url: 'https://rooflyzer.pro/features/marketing-dashboard',
    offers: { price: '0', description: 'Included in all plans' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Marketing Dashboard & Campaign Analytics"
        description="See every marketing touchpoint in one dashboard: lead sources, campaign ROI, email/SMS performance, and territory analytics."
        canonicalUrl="/features/marketing-dashboard"
        keywords="roofing marketing dashboard, roofing campaign analytics, roofing ROI tracking, contractor marketing analytics"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Marketing Dashboard', url: '/features/marketing-dashboard' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={BarChart3}>Built by Marketers</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Stop Looking at Spreadsheets.{' '}
              <span className="text-[#C29BF8]">Start Looking at Growth.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Our dashboard doesn't just show "jobs closed"; it shows cost-per-lead, channel
              performance, and ROI. It's the first CRM interface designed by marketers, for roofing
              business owners.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold shadow-md shadow-[#7950A2]/20 px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Get Your Dashboard
                <ArrowRight className="ml-2 size-5" />
              </Button>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.marketing} float={false} />
          </section>

          {/* What the dashboard tracks — product facts, demo-workspace framing */}
          <section className="mb-24">
            <div className="bg-black/40 border border-white/10 rounded-2xl p-4 md:p-8">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  // Only what the app ships today: lead-source coverage is live
                  // (the share of leads with a recorded source, and a count of
                  // those missing one); closed-revenue attribution and
                  // per-campaign cost wait on the pipeline and ad-spend
                  // connections, so they are labelled as coming.
                  {
                    value: 'Coverage',
                    label: 'Lead source',
                    desc: 'See which leads have a recorded source and which are missing one',
                  },
                  {
                    value: 'Coming',
                    label: 'Cost-per-lead',
                    desc: 'Per campaign, once ad spend and closed deals are connected',
                  },
                  { value: 'Ranked', label: 'Channel mix', desc: 'See what earns, cut what leaks' },
                  {
                    value: 'Monitored',
                    label: 'Reputation',
                    desc: 'Reviews across Google, Meta, industry sites',
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-white/5 rounded-xl p-4 border border-white/10"
                  >
                    <div className="text-lg font-bold text-white font-mono tracking-tight">
                      {stat.value}
                    </div>
                    <div className="text-white/80 text-xs mt-1 font-medium">{stat.label}</div>
                    <div className="text-white/45 text-xs mt-1 leading-relaxed">{stat.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Key Modules */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Everything Your Marketing Agency Should Be Showing You
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: DollarSign,
                  title: 'Revenue Attribution',
                  desc: 'Connect every closed deal back to the marketing channel that generated the lead. Know your true cost-per-acquisition.',
                  color: 'emerald',
                },
                {
                  icon: Search,
                  title: 'SEO Health Monitor',
                  desc: 'Track your rankings, page speed, and technical SEO issues. Get alerts when your visibility drops.',
                  color: 'blue',
                },
                {
                  icon: Star,
                  title: 'Reputation Manager',
                  desc: 'Monitor, respond to, and generate reviews across Google, Yelp, and social. Protect your 5-star reputation.',
                  color: 'amber',
                },
                {
                  icon: Megaphone,
                  title: 'Ad Spend Analytics',
                  desc: 'Google Ads, Facebook, LSA — see which campaigns produce revenue, not just clicks. Kill the losers, scale the winners.',
                  color: 'pink',
                },
                {
                  icon: PieChart,
                  title: 'Channel Mix Analysis',
                  desc: 'Understand your lead source breakdown. Are referrals outperforming paid ads? Now you will actually know.',
                  color: 'purple',
                },
                {
                  icon: Eye,
                  title: 'Competitor Intelligence',
                  desc: 'See where your competitors rank, what they bid on, and identify gaps you can exploit in your local market.',
                  color: 'cyan',
                },
              ].map((feature, i) => {
                const isEmerald = feature.color === 'emerald';
                const classes = isEmerald
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-slate-800 text-slate-300';
                return (
                  <div
                    key={i}
                    className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-slate-600 transition-colors"
                  >
                    <div
                      className={`w-12 h-12 rounded-xl ${classes.split(' ')[0]} flex items-center justify-center mb-4`}
                    >
                      <feature.icon className={`size-6 ${classes.split(' ')[1]}`} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Why Marketers Built This */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              Why a Marketing Agency Built a CRM
            </h2>
            <div className="max-w-3xl mx-auto space-y-6 text-white/80 leading-relaxed">
              <p>
                We started as{' '}
                <a
                  href="https://rogue-move.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:underline font-semibold"
                >
                  Rogue Move Digital Agency
                </a>
                , generating leads for roofing contractors. The problem? Every CRM we recommended
                was a black hole for the data we worked so hard to generate.
              </p>
              <p>
                Leads would go in, but attribution data didn't. Cost-per-lead? Nobody tracked it.
                Which campaign generated the $80K re-roof? Nobody knew. The CRMs were built by
                developers who understood databases but not the marketing journey.
              </p>
              <p>
                So we built one ourselves. The Marketing Dashboard in Rooflyzer isn't an
                afterthought — it's the <strong className="text-white">foundation</strong> the
                entire platform was built on.
              </p>
            </div>
          </section>

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="See Where Your Money Actually Goes"
            buttonLabel="Get Your Dashboard Free"
          >
            Stop trusting vanity metrics. Start making decisions based on revenue data.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default MarketingDashboardFeaturePage;
